import { useEffect, useState } from "react";
import { api, formatErr, API, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { handleSimpleFormNav } from "@/lib/keyboardNav";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, FileDown, Printer, Wrench, FileSpreadsheet, X, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { PaginationBar, usePagination } from "@/components/Pagination";

const statusColor = {
  Draft: "text-muted-foreground bg-secondary border-border",
  Sent: "text-blue-400 bg-blue-500/10 border-blue-500/40",
  Approved: "text-green-500 bg-green-500/10 border-green-500/40",
  Rejected: "text-red-500 bg-red-500/10 border-red-500/40",
};

const emptyForm = { booking_id: "", customer_id: "", vehicle_id: "", odometer: "", job_type: "", mechanic_id: "", complaints: "", gst_rate: 18, status: "Draft" };
const emptyCust = { name: "", phone: "", address: "" };
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export default function EstimateMaster() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [list, setList] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [jobTypes, setJobTypes] = useState([]);
  const [jobCardTypes, setJobCardTypes] = useState([]);
  const [items, setItems] = useState([]);
  const [mechanics, setMechanics] = useState([]);

  const [dateFrom, setDateFrom] = useState(todayISO());
  const [dateTo, setDateTo] = useState(todayISO());
  const [fStatus, setFStatus] = useState("all");

  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [f, setF] = useState(emptyForm);
  const [rateLines, setRateLines] = useState([]);
  const [partLines, setPartLines] = useState([]);
  const [selJT, setSelJT] = useState("");
  const [selItem, setSelItem] = useState("");
  const [selQty, setSelQty] = useState(1);
  const [selPrice, setSelPrice] = useState("");
  const [custOpen, setCustOpen] = useState(false);
  const [newCust, setNewCust] = useState(emptyCust);

  const canCreate = ["owner", "manager", "receptionist"].includes(user.role);
  const canEdit = ["owner", "manager"].includes(user.role);

  const load = () => api.get("/estimates").then(r => setList(r.data));
  useEffect(() => {
    load();
    api.get("/bookings").then(r => setBookings(r.data.filter(b => !b.job_id && b.status !== "cancelled"))).catch(() => {});
    api.get("/customers").then(r => setCustomers(r.data)).catch(() => {});
    api.get("/job-types").then(r => setJobTypes(r.data)).catch(() => {});
    api.get("/job-card-types").then(r => setJobCardTypes(r.data)).catch(() => {});
    api.get("/items?scope=all").then(r => setItems(r.data)).catch(() => {});
    if (["owner", "manager"].includes(user.role)) api.get("/users").then(r => setMechanics(r.data.filter(u => u.role === "mechanic"))).catch(() => {});
  }, []);
  useEffect(() => {
    if (f.customer_id) api.get(`/vehicles?customer_id=${f.customer_id}`).then(r => setVehicles(r.data));
    else setVehicles([]);
  }, [f.customer_id]);

  const filtered = list.filter(e =>
    (!dateFrom || e.date >= dateFrom) && (!dateTo || e.date <= dateTo) &&
    (fStatus === "all" || e.status === fStatus)
  );
  const pg = usePagination(filtered);

  const labourTotal = rateLines.reduce((s, l) => s + Number(l.labour_rate || 0), 0);
  const partsTotal = partLines.reduce((s, p) => s + Number(p.qty || 0) * Number(p.price || 0), 0);
  const subtotal = labourTotal + partsTotal;
  const gstAmt = subtotal * Number(f.gst_rate || 0) / 100;
  const total = subtotal + gstAmt;

  const pickBooking = (bid) => {
    const b = bookings.find(x => x.id === bid);
    if (!b) { setF(x => ({ ...x, booking_id: "" })); return; }
    setF(x => ({
      ...x, booking_id: bid,
      customer_id: b.customer_id || "", vehicle_id: b.vehicle_id || "",
      odometer: b.odometer ?? "", job_type: b.job_type || b.service_type || "",
      mechanic_id: b.mechanic_id || "", complaints: b.notes || "",
    }));
  };

  const addRate = () => {
    const jt = jobTypes.find(j => j.id === selJT);
    if (!jt) return;
    setRateLines(x => [...x, { job_type_id: jt.id, name: jt.name, labour_rate: jt.labour_rate }]);
    setSelJT("");
  };

  const addPart = () => {
    const it = items.find(i => i.id === selItem);
    if (!it) return;
    setPartLines(x => [...x, { item_id: it.id, name: it.item_name, qty: Number(selQty) || 1, price: Number(selPrice || it.mrp || 0) }]);
    setSelItem(""); setSelQty(1); setSelPrice("");
  };

  const edit = (e) => {
    setEditId(e.id);
    setF({
      booking_id: e.booking_id || "", customer_id: e.customer_id || "",
      vehicle_id: e.vehicle_id || "", odometer: e.odometer ?? "",
      job_type: e.job_type || e.service_type || "", mechanic_id: e.mechanic_id || "",
      complaints: e.complaints || "", gst_rate: e.gst_rate ?? 18, status: e.status || "Draft",
    });
    setRateLines(e.rate_lines || []);
    setPartLines(e.parts || []);
    setOpen(true);
  };

  const submit = async () => {
    if (!f.customer_id) { toast.error("Customer required"); return; }
    const payload = {
      booking_id: f.booking_id || null,
      customer_id: f.customer_id, vehicle_id: f.vehicle_id || null,
      odometer: f.odometer !== "" ? Number(f.odometer) : null,
      job_type: f.job_type || null, mechanic_id: f.mechanic_id || null,
      complaints: f.complaints || null,
      rate_lines: rateLines, parts: partLines,
      gst_rate: Number(f.gst_rate || 0), status: f.status,
    };
    try {
      if (editId) await api.patch(`/estimates/${editId}`, payload);
      else await api.post("/estimates", payload);
      toast.success(editId ? "Estimate updated" : "Estimate created");
      setF(emptyForm); setRateLines([]); setPartLines([]); setEditId(null); setOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const del = async (e) => {
    if (!confirm(`Delete estimate ${e.estimate_number}? This cannot be undone.`)) return;
    try { await api.delete(`/estimates/${e.id}`); toast.success("Estimate deleted"); load(); }
    catch (err) { toast.error(formatErr(err)); }
  };

  const convert = async (e) => {
    if (!confirm(`Convert estimate ${e.estimate_number} to a job card?`)) return;
    try {
      const { data } = await api.post(`/estimates/${e.id}/convert`);
      toast.success(`Job card ${data.job_number} created`);
      load();
      nav(`/jobcards?open=${data.job_id}`);
    } catch (err) { toast.error(formatErr(err)); }
  };

  const fetchPdf = async (e, download) => {
    try {
      const res = await fetch(`${API}/estimates/${e.id}/pdf`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("erp_token")}` }, credentials: "include",
      });
      if (!res.ok) throw new Error("PDF failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      if (download) {
        const a = document.createElement("a");
        a.href = url; a.download = `${e.estimate_number}.pdf`; a.click();
      } else window.open(url, "_blank");
    } catch (err) { toast.error(formatErr(err)); }
  };

  const saveCust = async () => {
    if (!newCust.name || !newCust.phone) { toast.error("Name & phone required"); return; }
    try {
      const { data } = await api.post("/customers", { name: newCust.name, phone: newCust.phone, address: newCust.address || null, email: null });
      await api.get("/customers").then(r => setCustomers(r.data));
      setF(x => ({ ...x, customer_id: data.id, vehicle_id: "" }));
      setNewCust(emptyCust); setCustOpen(false);
      toast.success("Customer added");
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="p-8">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><FileSpreadsheet className="w-3 h-3"/> Workshop · Quotations</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Estimate</h1>
        </div>
        {canCreate && (
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setF(emptyForm); setRateLines([]); setPartLines([]); setEditId(null); } }}>
            <DialogTrigger asChild>
              <Button data-testid="add-estimate-btn" className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] font-bold text-xs">
                <Plus className="w-4 h-4 mr-1"/> New Estimate
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm bg-card border-border max-w-3xl max-h-[90vh] overflow-y-auto" onKeyDownCapture={handleSimpleFormNav}>
              <DialogHeader><DialogTitle className="font-display uppercase">{editId ? "Edit" : "New"} Estimate</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">From Booking (optional — auto-fills details)</Label>
                  <Select value={f.booking_id} onValueChange={pickBooking}>
                    <SelectTrigger data-testid="est-booking" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue placeholder="Select booking"/></SelectTrigger>
                    <SelectContent>
                      {bookings.map(b => <SelectItem key={b.id} value={b.id}>{b.booking_number} · {b.customer_name} · {b.slot_date}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Customer *</Label>
                    <div className="flex gap-2 mt-1">
                      <Select value={f.customer_id} onValueChange={(v) => setF({ ...f, customer_id: v, vehicle_id: "" })}>
                        <SelectTrigger data-testid="est-customer" className="rounded-sm bg-secondary border-border flex-1"><SelectValue placeholder="Select customer"/></SelectTrigger>
                        <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name} · {c.phone}</SelectItem>)}</SelectContent>
                      </Select>
                      <Button type="button" data-testid="est-add-cust" onClick={() => setCustOpen(true)} variant="outline" className="rounded-sm border-primary/40 text-primary hover:bg-primary/10 shrink-0"><Plus className="w-4 h-4"/></Button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Vehicle</Label>
                    <Select value={f.vehicle_id} onValueChange={(v) => setF({ ...f, vehicle_id: v })}>
                      <SelectTrigger data-testid="est-vehicle" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue placeholder={f.customer_id ? "Select vehicle" : "Pick customer first"}/></SelectTrigger>
                      <SelectContent>{vehicles.map(v => <SelectItem key={v.id} value={v.id}>{v.registration} · {v.make} {v.model}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Odometer (km)</Label>
                    <Input data-testid="est-odometer" type="number" min="0" value={f.odometer} onChange={(e) => setF(x => ({ ...x, odometer: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Job Type</Label>
                    <Select value={f.job_type} onValueChange={(v) => setF({ ...f, job_type: v })}>
                      <SelectTrigger data-testid="est-job-type" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue placeholder="Select job type"/></SelectTrigger>
                      <SelectContent>
                        {jobCardTypes.map(jt => <SelectItem key={jt.id} value={jt.name}>{jt.name}</SelectItem>)}
                        {f.job_type && !jobCardTypes.some(jt => jt.name === f.job_type) && (
                          <SelectItem value={f.job_type}>{f.job_type}</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Mechanic</Label>
                    <Select value={f.mechanic_id} onValueChange={(v) => setF({ ...f, mechanic_id: v })}>
                      <SelectTrigger data-testid="est-mechanic" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue placeholder="Any"/></SelectTrigger>
                      <SelectContent>{mechanics.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Complaints / Notes</Label>
                  <Textarea data-testid="est-complaints" value={f.complaints} onChange={(e) => setF(x => ({ ...x, complaints: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" rows={2}/>
                </div>

                <div className="border border-border rounded-sm p-3">
                  <div className="text-xs uppercase tracking-[0.2em] text-primary mb-2">Job Rate (Labour)</div>
                  <div className="flex gap-2 mb-2">
                    <Select value={selJT} onValueChange={setSelJT}>
                      <SelectTrigger data-testid="est-jt-select" className="rounded-sm bg-secondary border-border flex-1"><SelectValue placeholder="Select job type"/></SelectTrigger>
                      <SelectContent>{jobTypes.map(jt => <SelectItem key={jt.id} value={jt.id}>{jt.name} · ₹{jt.labour_rate}</SelectItem>)}</SelectContent>
                    </Select>
                    <Button data-testid="est-add-rate" type="button" onClick={addRate} disabled={!selJT} className="rounded-sm bg-primary text-primary-foreground text-xs uppercase"><Plus className="w-3 h-3 mr-1"/> Add</Button>
                  </div>
                  {rateLines.map((l, i) => (
                    <div key={i} data-testid={`est-rate-line-${i}`} className="flex items-center justify-between text-xs border-t border-border py-1.5">
                      <span>{l.name}</span>
                      <span className="flex items-center gap-2 font-mono">{inr(l.labour_rate)}
                        <button data-testid={`est-rate-del-${i}`} onClick={() => setRateLines(x => x.filter((_, j) => j !== i))} className="text-destructive"><X className="w-3 h-3"/></button>
                      </span>
                    </div>
                  ))}
                </div>

                <div className="border border-border rounded-sm p-3">
                  <div className="text-xs uppercase tracking-[0.2em] text-primary mb-2">Parts (Item Master)</div>
                  <div className="flex gap-2 mb-2">
                    <Select value={selItem} onValueChange={(v) => { setSelItem(v); const it = items.find(i => i.id === v); setSelPrice(it ? String(it.mrp) : ""); }}>
                      <SelectTrigger data-testid="est-item-select" className="rounded-sm bg-secondary border-border flex-1"><SelectValue placeholder="Select item"/></SelectTrigger>
                      <SelectContent>{items.map(i => <SelectItem key={i.id} value={i.id}>{i.item_name} · MRP ₹{i.mrp}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input data-testid="est-item-qty" type="number" min="1" value={selQty} onChange={(e) => setSelQty(e.target.value)} className="rounded-sm bg-secondary border-border w-20" placeholder="Qty"/>
                    <Input data-testid="est-item-price" type="number" value={selPrice} onChange={(e) => setSelPrice(e.target.value)} className="rounded-sm bg-secondary border-border w-24" placeholder="Price"/>
                    <Button data-testid="est-add-part" type="button" onClick={addPart} disabled={!selItem} className="rounded-sm bg-primary text-primary-foreground text-xs uppercase"><Plus className="w-3 h-3 mr-1"/> Add</Button>
                  </div>
                  {partLines.map((p, i) => (
                    <div key={i} data-testid={`est-part-line-${i}`} className="flex items-center justify-between text-xs border-t border-border py-1.5">
                      <span>{p.name} × {p.qty}</span>
                      <span className="flex items-center gap-2 font-mono">{inr(p.qty * p.price)}
                        <button data-testid={`est-part-del-${i}`} onClick={() => setPartLines(x => x.filter((_, j) => j !== i))} className="text-destructive"><X className="w-3 h-3"/></button>
                      </span>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-2 gap-3 items-end">
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">GST %</Label>
                    <Input data-testid="est-gst" type="number" value={f.gst_rate} onChange={(e) => setF(x => ({ ...x, gst_rate: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Status</Label>
                    <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
                      <SelectTrigger data-testid="est-status" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        {(editId ? ["Draft", "Sent", "Approved", "Rejected"] : ["Draft", "Sent"]).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="border border-border rounded-sm p-3 text-xs font-mono space-y-1">
                  <div className="flex justify-between"><span className="text-muted-foreground">Labour</span><span>{inr(labourTotal)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Parts</span><span>{inr(partsTotal)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">GST @ {f.gst_rate || 0}%</span><span>{inr(gstAmt)}</span></div>
                  <div className="flex justify-between border-t border-border pt-1 text-sm font-bold"><span>TOTAL</span><span data-testid="est-total" className="text-primary">{inr(total)}</span></div>
                </div>

                <Button data-testid="save-estimate-btn" onClick={submit} disabled={!f.customer_id} className="w-full rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">{editId ? "Update Estimate" : "Save Estimate"}</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-4 border border-border bg-card rounded-sm p-3">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">From Date</Label>
          <Input data-testid="est-filter-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="mt-1 rounded-sm bg-secondary border-border w-40"/>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">To Date</Label>
          <Input data-testid="est-filter-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="mt-1 rounded-sm bg-secondary border-border w-40"/>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Status</Label>
          <Select value={fStatus} onValueChange={setFStatus}>
            <SelectTrigger data-testid="est-filter-status" className="mt-1 rounded-sm bg-secondary border-border w-36"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {["Draft", "Sent", "Approved", "Rejected"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button data-testid="est-filter-today" size="sm" variant="outline" onClick={() => { setDateFrom(todayISO()); setDateTo(todayISO()); }} className="rounded-sm text-xs uppercase tracking-wider">Today</Button>
        <Button data-testid="est-filter-all" size="sm" variant="outline" onClick={() => { setDateFrom(""); setDateTo(""); setFStatus("all"); }} className="rounded-sm text-xs uppercase tracking-wider">All Dates</Button>
        <div className="ml-auto text-xs text-muted-foreground font-mono">{filtered.length} of {list.length} estimates</div>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-hidden overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="text-left p-3">Estimate #</th>
              <th className="text-left p-3">Date</th>
              <th className="text-left p-3">Customer</th>
              <th className="text-left p-3">Vehicle</th>
              <th className="text-left p-3">Job Type</th>
              <th className="text-right p-3">Labour</th>
              <th className="text-right p-3">Parts</th>
              <th className="text-right p-3">GST</th>
              <th className="text-right p-3">Total</th>
              <th className="text-left p-3">Status</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={11} className="p-6 text-center text-muted-foreground">No estimates for the selected filters</td></tr>}
            {pg.pageItems.map(e => (
              <tr key={e.id} data-testid={`estimate-row-${e.id}`} className="border-t border-border hover:bg-secondary/50">
                <td className="p-3 font-mono text-xs text-primary">{e.estimate_number}</td>
                <td className="p-3 font-mono text-xs">{e.date}</td>
                <td className="p-3 text-xs">{e.customer_name}</td>
                <td className="p-3 text-xs">{e.vehicle_label || "-"}</td>
                <td className="p-3 text-xs">{e.job_type || e.service_type || "-"}</td>
                <td className="p-3 text-right font-mono text-xs">{inr(e.labour_total)}</td>
                <td className="p-3 text-right font-mono text-xs">{inr(e.parts_total)}</td>
                <td className="p-3 text-right font-mono text-xs">{inr(e.gst_amount)}</td>
                <td className="p-3 text-right font-mono font-semibold">{inr(e.total)}</td>
                <td className="p-3"><span data-testid={`estimate-status-${e.id}`} className={`px-2 py-1 text-[10px] uppercase tracking-wider rounded-sm border ${statusColor[e.status]}`}>{e.status}</span></td>
                <td className="p-3 text-right space-x-1 whitespace-nowrap">
                  {e.job_id ? (
                    <Button data-testid={`open-job-${e.id}`} size="sm" variant="ghost" onClick={() => nav(`/jobcards?open=${e.job_id}`)} className="h-7 text-xs text-primary"><Wrench className="w-3 h-3 mr-1"/> Job <ArrowRight className="w-3 h-3 ml-1"/></Button>
                  ) : (
                    <Button data-testid={`convert-${e.id}`} size="sm" variant="ghost" onClick={() => convert(e)} className="h-7 text-xs text-green-500"><Wrench className="w-3 h-3 mr-1"/> Convert</Button>
                  )}
                  <Button data-testid={`pdf-${e.id}`} size="sm" variant="ghost" onClick={() => fetchPdf(e, true)} className="h-7 w-7 p-0" title="Download PDF"><FileDown className="w-3 h-3"/></Button>
                  <Button data-testid={`print-${e.id}`} size="sm" variant="ghost" onClick={() => fetchPdf(e, false)} className="h-7 w-7 p-0" title="Print"><Printer className="w-3 h-3"/></Button>
                  {canEdit && <Button data-testid={`edit-estimate-${e.id}`} size="sm" variant="ghost" onClick={() => edit(e)} className="h-7 w-7 p-0"><Pencil className="w-3 h-3"/></Button>}
                  {canEdit && <Button data-testid={`del-estimate-${e.id}`} size="sm" variant="ghost" onClick={() => del(e)} className="h-7 w-7 p-0 text-destructive"><Trash2 className="w-3 h-3"/></Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <PaginationBar pg={pg}/>
      </div>

      <Dialog open={custOpen} onOpenChange={setCustOpen}>
        <DialogContent className="rounded-sm bg-card border-border max-w-md">
          <DialogHeader><DialogTitle className="font-display uppercase">New Customer</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Full Name</Label><Input data-testid="est-new-cust-name" value={newCust.name} onChange={(e) => setNewCust(x => ({ ...x, name: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/></div>
            <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Phone</Label><Input data-testid="est-new-cust-phone" value={newCust.phone} onChange={(e) => setNewCust(x => ({ ...x, phone: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/></div>
            <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Address</Label><Textarea value={newCust.address} onChange={(e) => setNewCust(x => ({ ...x, address: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" rows={2}/></div>
            <Button data-testid="est-save-new-cust" onClick={saveCust} className="w-full rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">Save & Select</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
